import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { ServiceProviderReceiptBatchOverviewTableView } from './ServiceProviderReceiptBatchOverviewTableView';
import {
  transformPhoenixListToLegacyList,
  useGenerateReceiptBatchPdf,
  useGetReceiptBatchListPhoenixNoSuspense,
} from '@pflegenavi/service-provider-frontend/api';
import { keepPreviousData } from '@tanstack/react-query';
import { ReceiptBatchTableEnlargeGallery } from './ReceiptBatchTableEnlargeGallery';
import { useGetBatchReceiptDateString } from '@pflegenavi/shared/frontend';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import { useUrlTableState } from '@pflegenavi/web-components';
import { useHandleFailedPreviewGenerationError } from '../../hooks/useHandleFailedPreviewGenerationError';
import { useReceiptBatchFilters } from './useReceiptBatchFilters';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface ServiceProviderReceiptBatchOverviewTableProps {}

export const ServiceProviderReceiptBatchOverviewTable: FC<
  ServiceProviderReceiptBatchOverviewTableProps
> = () => {
  const filtersState = useReceiptBatchFilters();
  const { page, pageSize, setPage, setPageSize } = useUrlTableState({
    defaultPageSize: 10,
  });

  const {
    data: phoenixData,
    isLoading,
    isError,
  } = useGetReceiptBatchListPhoenixNoSuspense(
    {
      page: page + 1, // Flop is 1-indexed, MUI TablePagination is 0-indexed
      page_size: pageSize,
      order_by: ['updated_on'],
      state: filtersState.state,
      filters: filtersState.filters,
    },
    { placeholderData: keepPreviousData }
  );

  const finalData =
    phoenixData && transformPhoenixListToLegacyList(phoenixData);

  const { getBatchReceiptDateString } = useGetBatchReceiptDateString();

  const transformedData = useMemo(() => {
    return (
      finalData?.data?.map((receiptBatch) => {
        return {
          ...receiptBatch,
          receiptDateString: getBatchReceiptDateString(
            receiptBatch.useIndividualDates,
            receiptBatch.minReceiptDate,
            receiptBatch.maxReceiptDate,
            receiptBatch.receiptDate
          ),
        };
      }) ?? []
    );
  }, [finalData?.data, getBatchReceiptDateString]);

  const [selectedBatchTenant, setSelectedBatchTenant] = useState<
    string | undefined
  >(undefined);
  const [receiptImageIds, setReceiptImageIds] = useState<string[] | undefined>(
    undefined
  );
  const [imageGalleryTitle, setImageGalleryTitle] = useState<
    string | undefined
  >(undefined);

  const [openImageGallery, setOpenImageGallery] = useState(false);

  const [generatedReceiptImageUrls, setGeneratedReceiptImageUrls] = useState<
    string[]
  >([]);
  const [generatedReceiptTypeMap, setGeneratedReceiptTypeMap] =
    useState<UrlTypeNameMapping>(new Map());

  const { mutateAsync: generateReceiptBatch } = useGenerateReceiptBatchPdf();
  const { handleFailedPreviewGenerationError } =
    useHandleFailedPreviewGenerationError();

  const generateReceiptBatchPreview = useCallback(
    async (batchId: string, filename: string) => {
      try {
        const receiptBlob = await generateReceiptBatch({
          batchId,
        });
        if (receiptBlob) {
          const file = window.URL.createObjectURL(receiptBlob);

          setGeneratedReceiptImageUrls([file]);
          setGeneratedReceiptTypeMap(
            new Map([
              [
                file,
                {
                  type: 'application/pdf',
                  data: Promise.resolve(receiptBlob),
                  name: filename,
                },
              ],
            ])
          );
          setOpenImageGallery(true);
        }
      } catch (e) {
        handleFailedPreviewGenerationError(e);
      }
    },
    [generateReceiptBatch, handleFailedPreviewGenerationError]
  );

  const onClickReceiptIcon = useCallback(
    async ({
      id,
      tenant,
      receiptImageIds,
      title,
      isPreview,
    }: {
      id: string;
      tenant: string;
      receiptImageIds: string[] | undefined;
      title: string;
      isPreview?: boolean;
    }) => {
      if (isPreview) {
        await generateReceiptBatchPreview(id, title); // Title contains the filename when in preview mode
        return;
      }
      setSelectedBatchTenant(tenant);
      setReceiptImageIds(receiptImageIds);
      setImageGalleryTitle(title);
      if (receiptImageIds && receiptImageIds.length > 0) {
        setOpenImageGallery(true);
      }
    },
    [generateReceiptBatchPreview]
  );

  return (
    <>
      <ServiceProviderReceiptBatchOverviewTableView
        data={transformedData}
        totalRowCount={finalData?.meta?.totalItems ?? 0}
        page={page}
        pageSize={pageSize}
        onChangePage={setPage}
        onChangePageSize={setPageSize}
        isLoading={isLoading}
        isError={isError}
        onClickReceiptIcon={onClickReceiptIcon}
        filtersState={filtersState}
      />
      <ReceiptBatchTableEnlargeGallery
        selectedBatchTenant={selectedBatchTenant}
        openImageGallery={openImageGallery}
        setOpenImageGallery={setOpenImageGallery}
        batchImageIds={receiptImageIds}
        title={imageGalleryTitle ?? ''}
        generatedReceiptImageUrls={generatedReceiptImageUrls}
        setGeneratedReceiptImageUrls={setGeneratedReceiptImageUrls}
        generatedReceiptTypeMap={generatedReceiptTypeMap}
      />
    </>
  );
};
